import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import { ChangeEvent, useCallback, useState } from "react";
import history from "utils/history";
import { KeyboardBackspace } from "@mui/icons-material";
import { getStatusColor } from "../Components/EmailDataList";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import ViewEmail from "./Components/ViewEmail";
import LinkActivity from "./Components/LinkActivity";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import RetargetModal from "./Components/RetargetModal";
import ExportModal from "./Components/ExportModal";
import { CampaignCount, CampaignOverviewType } from "models/interfaces";
import RetargetedCampaigns from "./Components/RetargetedCampaigns";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import Overview from "./Components/Overview";
import { campaignStatusEnum } from "../Constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import campaignStyles from "../Campaigns.styles";
import { MoreActionsInCampaign } from "../CampaignConstants";

export const tabs = () => [
  {
    label: "Overview",
    value: "overview",
    key: "overview",
  },
  {
    label: "View Email",
    value: "view",
    key: "view",
  },
  {
    label: "Link Activity",
    value: "link",
    key: "link",
  },
  {
    label: "Retargeted Campaigns",
    value: "retargeted-campaigns",
    key: "retargeted-campaigns",
  },
];

interface CustomProps {
  campaignData: CampaignOverviewType;
  setCampaignData: Function;
  failedMetrics: CampaignCount[];
  failedMetricsNew: number;
  parentCampaignType?: string;
}

const ViewRegularCampaign = (props: CustomProps) => {
  const theme = useTheme();
  const classes = campaignStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(tabs()[0].key);
  const [retargetModalOpen, setRetargetModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const notOpenedCount: any =
    (props.campaignData?.campaignCounts.find((item) => item.metricId === 1)
      ?.count || 0) -
    (props.campaignData?.campaignCounts.find((item) => item.metricId === 4)
      ?.count || 0) -
    (props.campaignData?.campaignCounts.find((item) => item.metricId === 5)
      ?.count || 0);
  const hasCampaignAddAccess = hasAccessTo(
    strings.campaign,
    strings.addPermission
  );
  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      setCurrentTab(value);
    },
    []
  );

  const handleExportModal = () => {
    setExportModalOpen(true);
  };

  const getActionRate = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed() + "%";
  };

  const getBackButton = () => {
    return (
      <Stack direction="row" alignItems="center">
        <Link
          color="text.primary"
          href={viewpaths.campaignsViewPath}
          sx={{
            alignItems: "center",
            display: "inline-flex",
          }}
          onClick={(e) => {
            e.preventDefault();
            history.goBack();
          }}
          underline="hover"
          component={RouterLink}
        >
          <KeyboardBackspace
            htmlColor="#475569"
            sx={{ cursor: "pointer", pr: 1 }}
          />
          <Typography variant="body1" color="text.primary" fontWeight={700}>
            Campaigns
          </Typography>
        </Link>
      </Stack>
    );
  };

  const getStatsHeaderDesktop = () => {
    return (
      <>
        {getBackButton()}
        <Stack
          direction={isDesktop ? "row" : "column"}
          justifyContent={"space-between"}
          alignItems={isDesktop ? "center" : "flex-start"}
          mt={2}
        >
          <Stack direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Tooltip title={props.campaignData?.campaign?.name} arrow>
                <Typography
                  color="text.primary"
                  fontSize={"1.375rem"}
                  fontWeight={600}
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="300px"
                  display="block"
                >
                  {props.campaignData?.campaign?.name}
                </Typography>
              </Tooltip>
              <Chip
                label={
                  campaignStatusEnum[props.campaignData?.campaign?.status] ??
                  props.campaignData?.campaign?.status
                }
                sx={{
                  color: getStatusColor(
                    props.campaignData?.campaign?.status ?? "All"
                  ),
                  borderRadius: "5px",
                  minWidth: "90px",
                }}
                size="medium"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                {props.campaignData?.campaign.type} Campaign
              </Typography>
              <FiberManualRecordIcon
                fontSize="small"
                sx={{ width: "8px", color: theme.palette.primary.dark }}
              />{" "}
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                Scheduled on{" "}
                {convertESTtoUserLocalDateAndTime(
                  props.campaignData?.campaign?.scheduleTime,
                  "MMMM D, YYYY [at] h:mm A"
                )}
              </Typography>
            </Stack>
          </Stack>{" "}
          <Stack direction="row" spacing={2}>
            {props.campaignData?.campaign?.status !== "InProgress" && (
              <Button
                variant="text"
                startIcon={<FileUploadOutlinedIcon />}
                onClick={handleExportModal}
              >
                Export Report
              </Button>
            )}
            {hasCampaignAddAccess &&
              props?.parentCampaignType !== "ABTesting" &&
              props.campaignData?.campaign?.status !== "InProgress" && (
                <>
                  <Tooltip
                    title="Send the same campaign again to the original recipients or a different recipients."
                    arrow
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() =>
                        history.push(
                          `${viewpaths.createResendCampaignViewPath}?id=${props.campaignData.campaign.id}`
                        )
                      }
                    >
                      Resend
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title="Engage recipients based on their interaction—send follow-ups to non-openers or targeted emails to those who clicked."
                    arrow
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setRetargetModalOpen(true)}
                    >
                      Retarget
                    </Button>
                  </Tooltip>
                </>
              )}
          </Stack>
        </Stack>
      </>
    );
  };

  const getStatsHeaderMobile = () => {
    return (
      <>
        {getBackButton()}
        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          mt={2}
        >
          <Stack direction="column" spacing={1} width="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              justifyContent="space-between"
            >
              <Tooltip title={props.campaignData?.campaign?.name} arrow>
                <Typography
                  color="text.primary"
                  fontSize={"1.375rem"}
                  fontWeight={600}
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="300px"
                  display="block"
                >
                  {props.campaignData?.campaign?.name}
                </Typography>
              </Tooltip>
              <IconButton
                size="small"
                sx={{
                  color: theme.palette.primary.dark,
                  padding: 0,
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
            <Box>
              <Chip
                label={
                  campaignStatusEnum[props.campaignData?.campaign?.status] ??
                  props.campaignData?.campaign?.status
                }
                sx={{
                  color: getStatusColor(
                    props.campaignData?.campaign?.status ?? "All"
                  ),
                  borderRadius: "5px",
                  minWidth: "90px",
                }}
                size="medium"
              />
            </Box>
            <Stack direction="column" spacing={1}>
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                {props.campaignData?.campaign.type} Campaign
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.primary.dark}
                gutterBottom
              >
                Scheduled on{" "}
                {convertESTtoUserLocalDateAndTime(
                  props.campaignData?.campaign?.scheduleTime,
                  "MMMM D, YYYY [at] h:mm A"
                )}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </>
    );
  };

  const getActionTabs = () => {
    const filteredTabs = tabs().filter(
      (tab) =>
        props.parentCampaignType !== "ABTesting" ||
        tab.value !== "retargeted-campaigns"
    );
    return (
      <Tabs
        indicatorColor="primary"
        onChange={handleTabsChange}
        scrollButtons="auto"
        textColor="primary"
        value={currentTab}
        variant="scrollable"
        sx={{ mt: 2 }}
      >
        {filteredTabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            disabled={isLoading}
          />
        ))}
      </Tabs>
    );
  };

  const getTabsBody = () => {
    switch (currentTab) {
      case "overview":
        return (
          <Overview
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            campaign={props.campaignData!}
            setCampaign={props.setCampaignData}
            failedMetrics={props.failedMetrics}
            failedMetricsNew={props.failedMetricsNew}
          />
        );
      case "view": {
        return (
          <ViewEmail
            id={Math.abs(props.campaignData?.campaign?.id!)}
            containsParentId={props.campaignData?.campaign.parentId !== 0}
            requestedCount={
              props.campaignData?.campaignCounts?.find(
                (item) => item.metricId === 1
              )?.count || 0
            }
          />
        );
      }
      case "link": {
        return (
          <Card>
            <LinkActivity
              id={Math.abs(props.campaignData?.campaign?.id!)}
              showTotalClicks
            />
          </Card>
        );
      }
      case "retargeted-campaigns": {
        return (
          <Card>
            <RetargetedCampaigns
              id={Math.abs(props.campaignData?.campaign?.id!)}
            />
          </Card>
        );
      }
      default: {
        return (
          <Overview
            currentTab={"overview"}
            setCurrentTab={setCurrentTab}
            campaign={props.campaignData!}
            setCampaign={props.setCampaignData}
            failedMetrics={props.failedMetrics}
            failedMetricsNew={props.failedMetricsNew}
          />
        );
      }
    }
  };

  const handleMenuItemClick = async (action: string) => {
    switch (action) {
      case "retarget":
        setAnchorEl(null);
        setRetargetModalOpen(true);
        break;
      case "resend":
        setAnchorEl(null);
        history.push(
          `${viewpaths.createResendCampaignViewPath}?id=${props.campaignData.campaign.id}`
        );
        break;
      case "export-report":
        setAnchorEl(null);
        handleExportModal();
        break;
      default:
        break;
    }
  };

  const moreOptions = () => {
    return (
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {MoreActionsInCampaign(
          props.campaignData.campaign.type,
          props.campaignData.campaign.status,
          hasCampaignAddAccess
        ).map((item, index) => (
          <MenuItem
            key={index}
            value={item.value}
            sx={classes.dropDownMenuItem}
            onClick={() => handleMenuItemClick(item.value)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {isDesktop ? getStatsHeaderDesktop() : getStatsHeaderMobile()}
        {getActionTabs()}
        <Divider flexItem sx={{ mb: 2, backgroundColor: "#47556940" }} />
        {getTabsBody()}
      </CenteredAuthenticatedLayout>
      <RetargetModal
        open={retargetModalOpen}
        setOpen={setRetargetModalOpen}
        id={props.campaignData?.campaign?.id}
        campaignName={props.campaignData?.campaign?.name ?? ""}
        successCount={
          props.campaignData?.campaignCounts.find((item) => item.metricId === 3)
            ?.count || 0
        }
        openedCount={
          props.campaignData?.campaignCounts.find((item) => item.metricId === 5)
            ?.count || 0
        }
        clickedCount={
          props.campaignData?.campaignCounts.find((item) => item.metricId === 6)
            ?.count || 0
        }
        openedRate={getActionRate(
          props.campaignData?.campaignCounts.find((item) => item.metricId === 5)
            ?.count || 0,
          props.campaignData?.campaignCounts.find((item) => item.metricId === 3)
            ?.count || 1
        )}
        clickedRate={getActionRate(
          props.campaignData?.campaignCounts.find((item) => item.metricId === 6)
            ?.count || 0,
          props.campaignData?.campaignCounts.find((item) => item.metricId === 3)
            ?.count || 1
        )}
        notOpenedRate={getActionRate(
          notOpenedCount ?? 0,
          props.campaignData?.campaignCounts.find((item) => item.metricId === 3)
            ?.count || 1
        )}
        isFollowUp={
          (props.campaignData?.campaignCounts.find(
            (item) => item.metricId === 3
          )?.count || 0) !==
          (props.campaignData?.campaignCounts.find(
            (item) => item.metricId === 5
          )?.count || 0)
        }
      />
      <ExportModal
        open={exportModalOpen}
        setOpen={setExportModalOpen}
        options={["All", "Opened", "Clicked", "Unsubscribed", "Failed"]}
        campaignId={props.campaignData?.campaign?.id}
        counts={props.campaignData?.campaignCounts}
      />
      {moreOptions()}
    </>
  );
};

export default ViewRegularCampaign;
